import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import RegistrationForms from './components/RegistrationForms';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import WhatsAppIcon from './components/WhatsAppIcon';
import UpdatesButton from './components/UpdatesButton';
import UpdatesSidebar from './components/UpdatesSidebar';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import BlogPost from './components/BlogPost';
import PageTracker from './components/PageTracker';
import { auth } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { initializeGA4 } from './utilities/googleAnalyticsUtils';

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Initialize GA4 on app mount
    initializeGA4();
    
    // Set up Firebase auth state observer
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        const userData = {
          uid: user.uid,
          email: user.email,
          name: user.displayName || 'Admin'
        };
        setUser(userData);
      } else {
        // User is signed out
        setUser(null);
      }
      setLoading(false);
    });

    // Clean up the listener when component unmounts
    return () => unsubscribe();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return <div className="loading-app">Loading...</div>;
  }

  return (
    <div className="App">
      <PageTracker />
      <Routes>
        <Route path="/admin/login" element={
          <>
            <AdminRoute />
            {user ? <Navigate to="/admin/dashboard" /> : <AdminLogin onLogin={handleLogin} />}
          </>
        } />
        
        <Route path="/admin/dashboard" element={
          <>
            <AdminRoute />
            {user ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/admin/login" />}
          </>
        } />
        
        <Route path="/blog/:id" element={
          <>
            <BlogPost />
            <Footer />
            <WhatsAppIcon />
          </>
        } />
        
        {/* Redirect /home to / to ensure proper tracking */}
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/home/" element={<Navigate to="/" replace />} />
        
        <Route path="/" element={
          <>
            <Header />
            <main>
              <HeroSection />
              <AboutSection />
              <RegistrationForms />
              <ContactSection />
            </main>
            <Footer />
            <WhatsAppIcon />
            <UpdatesButton toggleSidebar={toggleSidebar} />
            <UpdatesSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
          </>
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
